import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import ArticleBody from "@/components/feature/ArticleBody";
import ArticleToc from "@/components/feature/ArticleToc";
import ArticleCard from "@/components/feature/ArticleCard";
import NewsletterForm from "@/components/feature/NewsletterForm";
import { getArticleById, articles } from "@/mocks/articles";
import { getCategoryBySlug } from "@/mocks/categories";
import { buildBreadcrumbSchema } from "@/lib/seo";
import { formatDate, formatNumber, getRelatedArticles, tintMap } from "@/lib/content";
import { SITE_URL } from "@/mocks/site";

export default function ArticleDetail() {
  const { id = "" } = useParams();
  const article = getArticleById(id);

  const headings = useMemo(() => {
    if (!article) return [];
    let index = 0;
    return article.blocks
      .filter((block) => block.type === "h2")
      .map((block) => {
        index += 1;
        return { id: `sec-${index}`, text: block.text ?? "" };
      });
  }, [article]);

  if (!article) {
    return (
      <section className="bg-background-50">
        <div className="mx-auto w-full max-w-[1240px] px-4 md:px-6 py-24 text-center">
          <h1 className="font-heading text-3xl text-foreground-950">看点不存在或已下线</h1>
          <p className="mt-3 text-sm text-foreground-600">这条看点可能已被调整，可以回今日看点看看最新内容。</p>
          <Link
            to="/today"
            className="mt-6 inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>返回今日看点
          </Link>
        </div>
      </section>
    );
  }

  const category = getCategoryBySlug(article.categorySlug);
  const tint = tintMap[category?.tint ?? "primary"];
  const related = getRelatedArticles(article, 3);
  const ranking = [...articles].sort((a, b) => b.views - a.views).slice(0, 4);

  const jsonLd = [
    buildBreadcrumbSchema([
      { name: "首页", path: "/" },
      { name: "主题分类", path: "/topics" },
      { name: category?.name ?? "看点", path: `/topic/${article.categorySlug}` },
      { name: article.title, path: `/article/${article.id}` },
    ]),
    {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: article.title,
      description: article.excerpt,
      image: article.cover,
      datePublished: article.date,
      dateModified: article.date,
      inLanguage: "zh-CN",
      author: { "@type": "Organization", name: article.author },
      publisher: { "@type": "Organization", name: "黑料看点" },
      articleSection: category?.name,
      keywords: article.tags.join(","),
      mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE_URL}/article/${article.id}` },
    },
  ];

  return (
    <>
      <Seo
        title={`${article.title} - 黑料看点`}
        description={article.excerpt}
        keywords={article.tags.join(",")}
        path={`/article/${article.id}`}
        type="article"
        image={article.cover}
        jsonLd={jsonLd}
      />

      <article>
        <header className="bg-background-100 border-b border-background-200">
          <div className="mx-auto w-full max-w-[1240px] px-4 md:px-6 pt-6 pb-10 md:pt-8 md:pb-12">
            <Breadcrumbs
              items={[
                { name: "主题分类", path: "/topics" },
                { name: category?.name ?? "看点", path: `/topic/${article.categorySlug}` },
                { name: article.title },
              ]}
            />

            <div className="mt-6 max-w-3xl">
              <div className="flex flex-wrap items-center gap-2">
                <Link
                  to={`/topic/${article.categorySlug}`}
                  className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs ${tint.chipBg} ${tint.chipText} hover:opacity-80 transition-opacity`}
                >
                  <i className={category?.icon}></i>
                  {category?.name}
                </Link>
                {article.hot && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs bg-primary-500 text-background-50">
                    <i className="ri-fire-line"></i>今日必看
                  </span>
                )}
              </div>

              <h1 className="mt-4 font-heading text-2xl md:text-4xl leading-snug text-foreground-950">
                {article.title}
              </h1>
              <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-600">{article.excerpt}</p>

              <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs md:text-sm text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line"></i>
                  {article.author} · {article.authorRole}
                </span>
                <span className="flex items-center gap-1.5"><i className="ri-calendar-line"></i>{formatDate(article.date)}</span>
                <span className="flex items-center gap-1.5"><i className="ri-time-line"></i>{article.readTime}</span>
                <span className="flex items-center gap-1.5"><i className="ri-eye-line"></i>{formatNumber(article.views)} 阅读</span>
                <span className="flex items-center gap-1.5"><i className="ri-chat-3-line"></i>{article.comments} 评论</span>
              </div>
            </div>

            <div className="mt-8 w-full aspect-[16/9] rounded-lg overflow-hidden bg-background-200">
              <img src={article.cover} alt={article.coverAlt} className="w-full h-full object-cover object-top" />
            </div>
          </div>
        </header>

        <div className="bg-background-50">
          <div className="mx-auto w-full max-w-[1240px] px-4 md:px-6 py-12 md:py-16">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
              <div className="lg:col-span-8">
                <ArticleBody blocks={article.blocks} />

                <div className="mt-10 pt-6 border-t border-background-200">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs text-foreground-500 mr-1">标签</span>
                    {article.tags.map((tag) => (
                      <Link
                        key={tag}
                        to={`/today?q=${encodeURIComponent(tag)}`}
                        className="px-3 py-1.5 rounded-full bg-background-100 border border-background-200 text-xs text-foreground-700 hover:border-primary-300 hover:text-primary-700 transition-colors"
                      >
                        {tag}
                      </Link>
                    ))}
                  </div>
                </div>

                <div className="mt-8 rounded-lg bg-background-100 border border-background-200 p-6 flex flex-col sm:flex-row gap-4 sm:items-center">
                  <span className="w-12 h-12 flex items-center justify-center rounded-md bg-primary-100 text-primary-700 shrink-0">
                    <i className="ri-quill-pen-line text-xl"></i>
                  </span>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-foreground-950">{article.author}</p>
                    <p className="mt-1 text-xs leading-relaxed text-foreground-600">
                      {article.authorRole}。内容围绕公开作品与公开讨论展开，力求把事件讲清楚，而不是把情绪放大。
                    </p>
                  </div>
                </div>
              </div>

              <aside className="lg:col-span-4">
                <div className="lg:sticky lg:top-24 flex flex-col gap-6">
                  <ArticleToc headings={headings} />

                  <div className="rounded-lg bg-background-100 border border-background-200 p-5 md:p-6">
                    <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground-950 pb-3 border-b border-background-200">
                      <i className="ri-fire-line text-primary-600"></i>
                      热门阅读
                    </h2>
                    <div className="mt-1">
                      {ranking.map((item, index) => (
                        <ArticleCard key={item.id} article={item} variant="minimal" rank={index + 1} />
                      ))}
                    </div>
                  </div>

                  <div className="rounded-lg bg-secondary-950 p-5 md:p-6">
                    <h2 className="text-sm font-medium text-accent-300">订阅看点日报</h2>
                    <p className="mt-2 mb-4 text-xs leading-relaxed text-background-100/80">
                      每天早上把精选看点清单发到你的邮箱。
                    </p>
                    <NewsletterForm tone="dark" compact />
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </div>

        <section className="bg-background-100 border-t border-background-200">
          <div className="mx-auto w-full max-w-[1240px] px-4 md:px-6 py-12 md:py-16">
            <div className="flex items-end justify-between mb-8">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950">相关看点推荐</h2>
              <Link to="/today" className="inline-flex items-center gap-1.5 text-sm text-foreground-700 hover:text-primary-700 whitespace-nowrap transition-colors">
                更多看点 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-7">
              {related.map((item) => (
                <ArticleCard key={item.id} article={item} variant="default" />
              ))}
            </div>
          </div>
        </section>
      </article>
    </>
  );
}